import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, MapPin, Phone, Mail } from "lucide-react";
import { FaFacebookF } from "react-icons/fa";
import { BsInstagram, BsLinkedin, BsTwitterX } from "react-icons/bs";
import { contacts } from "../constants/contacts";

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300 pt-16 pb-12 mt-12 rounded-t-[50px] shadow-[0_-20px_40px_rgba(0,0,0,0.1)] text-left">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-3 rounded-2xl text-white shadow-lg shadow-indigo-500/30">
                <GraduationCap className="h-6 w-6" />
              </div>
              <span className="font-extrabold text-2xl tracking-tight text-white">
                Dee Novo
              </span>
            </Link>
            <p className="text-gray-400 leading-relaxed text-sm font-medium pr-4">
              Empowering the next generation of thinkers, creators, and leaders
              through innovative education and character development.
            </p>
            <div className="flex space-x-4 pt-2">
              {[
                <FaFacebookF size={18} />,
                <BsTwitterX size={18} />,
                <BsInstagram size={18} />,
                <BsLinkedin size={18} />,
              ].map((icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="h-10 w-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-indigo-500 hover:text-white transition-all hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(99,102,241,0.3)]"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-white tracking-wide">
              Quick Links
            </h3>
            <ul className="space-y-4">
              {[
                { name: "About Us", path: "/" },
                { name: "School at a Glance", path: "/school-at-a-glance" },
                { name: "Academics", path: "/academics" },
                { name: "Admissions", path: "/admissions" },
                { name: "Activities @ Deenovo", path: "/activities" },
                { name: "Verify Id Card", path: "/verify/idCard" },
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="text-gray-400 font-medium hover:text-indigo-400 transition-colors text-sm flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mr-3 group-hover:bg-indigo-400 transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-white tracking-wide">
              Contact Us
            </h3>
            <div className="bg-gray-800/50 p-6 rounded-[28px] space-y-5 border border-gray-700/50">
              <div className="flex items-start space-x-4 text-gray-400 text-sm font-medium">
                <div className="bg-gray-800 p-2.5 rounded-xl text-indigo-400 shrink-0 border border-gray-700">
                  <MapPin size={16} />
                </div>
                <span className="mt-1 leading-relaxed">{contacts.address}</span>
              </div>
              <div className="flex items-center space-x-4 text-gray-400 text-sm font-medium">
                <div className="bg-gray-800 p-2.5 rounded-xl text-indigo-400 shrink-0 border border-gray-700">
                  <Phone size={16} />
                </div>
                <div className="flex flex-col">
                  <a href={`tel:${contacts.mobile1}`}>{contacts.mobile1}</a>
                  <a href={`tel:${contacts.mobile2}`}>{contacts.mobile2}</a>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-gray-400 text-sm font-medium">
                <div className="bg-gray-800 p-2.5 rounded-xl text-indigo-400 shrink-0 border border-gray-700">
                  <Mail size={16} />
                </div>
                <a href={`mailto:${contacts.email}`}>{contacts.email}</a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-white tracking-wide">
              Newsletter
            </h3>
            <p className="text-gray-400 font-medium text-sm mb-6 leading-relaxed">
              Subscribe to our newsletter to get latest updates and news right
              in your inbox.
            </p>
            <form
              className="flex flex-col space-y-4"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="bg-gray-800 rounded-2xl px-4 py-3 flex items-center border border-gray-700 focus-within:border-indigo-500 transition-colors">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-transparent border-none w-full focus:outline-none text-white placeholder-gray-500 text-sm font-medium"
                />
              </div>
              <button className="px-6 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl transition-all text-sm w-full shadow-lg shadow-indigo-600/20 active:scale-[0.98]">
                Subscribe Now
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <p className="text-gray-500 font-medium text-sm">
            &copy; 2026 Dee Novo School. All rights
            reserved.
          </p>

          {/* <p className="text-gray-400 font-semibold text-xs sm:text-sm bg-gray-800/80 px-4 py-2 rounded-xl border border-gray-700/60">
            Designed &amp; Developed by{" "}
            <a
              href="tel:918709278485"
              className="text-indigo-400 hover:text-indigo-300 font-bold transition-colors"
            >
              Jitendra Kr | 8709278485
            </a>
          </p> */}
          <div></div>

          <div className="flex space-x-6 text-sm font-semibold text-gray-500">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
